<template>
  <div class="parameters-display">
    <!-- Parameters Table -->
    <table class="parameters-table table table-sm table-bordered">
      <thead>
        <tr>
          <th>
            <translate translate-context="email_templates">Parameter</translate>
          </th>
          <th>
            <translate translate-context="email_templates">Description / Value</translate>
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- Parameters -->
        <tr v-for="(param, index) in flatParameters" :key="index">
          <td>
            <div class="d-flex align-items-center">
              <CIcon
                name="cil-copy"
                size="sm"
                class="copy-icon mr-2"
                @click="copyToClipboard(formatParameter(param.key))"
                v-c-tooltip="$gettext('Copy to clipboard')"/>
              <code class="parameter-key">
                {{ formatParameter(param.key) }}
              </code>
            </div>
          </td>
          <td class="text-muted">
            {{ param.description }}
          </td>
        </tr>

        <!-- Translation Keys -->
        <tr v-for="key in translationKeys" :key="'t_' + key">
          <td>
            <div class="d-flex align-items-center">
              <CIcon
                name="cil-copy"
                size="sm"
                class="copy-icon mr-2"
                @click="copyToClipboard(formatTranslation(key))"
                v-c-tooltip="$gettext('Copy to clipboard')"/>
              <code class="parameter-key parameter-key-translation">{{ formatTranslation(key) }}</code>
            </div>
          </td>
          <td class="text-muted">
            <slot name="translation-preview" :translationKey="key"></slot>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Django/SES Template Explanation (only if arrays exist) -->
    <CAlert v-if="hasArrayParameters" color="info" class="mt-3 mb-0">
      <div class="d-flex align-items-start">
        <CIcon name="cilLightbulb" size="lg" class="mr-3 flex-shrink-0 mt-1"/>
        <div>
          <strong>
            <translate translate-context="email_templates">How to use template parameters</translate>
          </strong>
          <p class="mb-0 mt-2 small">
            <translate translate-context="email_templates">
              This service uses Django template syntax. Use double curly braces to insert parameters:
            </translate>
          </p>
          <pre class="mb-0 mt-2 small bg-light p-2 rounded" v-pre><code>Hello {{ user_name }},

Your order total is {{ cart_total_price }}.

{% for item in items %}
  - {{ item.event_name }}: {{ item.price }}
{% endfor %}</code></pre>
        </div>
      </div>
    </CAlert>
  </div>
</template>

<script>
export default {
  name: 'DefaultParametersDisplay',
  props: {
    availableParameters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    translationKeys: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  computed: {
    hasArrayParameters() {
      // Check if there are any array parameters (containing [])
      return Object.keys(this.availableParameters).some(key => key.includes('[]'))
    },
    flatParameters() {
      // Return flat structure
      return Object.entries(this.availableParameters).map(([key, description]) => ({
        key,
        description,
        type: 'simple'
      }))
    }
  },
  methods: {
    formatParameter(key) {
      // Django/Default: {{ code }}
      return '{{ ' + key + ' }}'
    },
    formatTranslation(key) {
      // Django/Default: {{ t_key }}
      return '{{ t_' + key + ' }}'
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.notifySuccess(this.$pgettext('email_templates', 'Copied to clipboard'))
      }).catch(() => {
        this.notifyError(this.$pgettext('email_templates', 'Failed to copy to clipboard'))
      })
    }
  }
}
</script>

<style scoped>
.parameters-display {
  width: 100%;
}

.parameters-table {
  background: white;
  margin-bottom: 0;
}

.parameters-table th {
  background: #e9ecef;
  font-weight: 600;
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.parameters-table td {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.parameter-key {
  background: #f1f3f5;
  color: #e83e8c;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.875rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.parameter-key-translation {
  color: #6610f2;
}

.copy-icon {
  cursor: pointer;
  color: #6c757d;
  transition: color 0.15s;
}

.copy-icon:hover {
  color: #007bff;
}
</style>
